// 문서 자동 요약 페이지
import { useState } from "react";
import { useParams } from "react-router-dom";
import { FileText, Upload, Sparkles, Loader2 } from "lucide-react";
import PageHeader from "../../components/common/ui/PageHeader";
import api from "../../api/axios";

export default function DocumentSummary() {
  const { id: projectId } = useParams();
  const [file, setFile] = useState(null);
  const [summary, setSummary] = useState("");
  const [loading, setLoading] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setSummary("");
    }
  };

  const handleSummarize = async () => {
    if (!file) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("project", projectId);

      // TODO: 실제 API 엔드포인트로 변경
      const response = await api.post("/ai/document/summary/", formData);
      setSummary(response.data.summary);
    } catch (error) {
      console.error("Summary error:", error);
      setSummary("문서 요약 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <PageHeader>
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6" />
          <span>문서 자동 요약</span>
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 업로드 영역 */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">문서 업로드</h2>

          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-purple-500 transition-colors">
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-sm text-gray-600 mb-4">
              PDF, DOCX 파일을 선택하세요
            </p>
            <input
              type="file"
              accept=".pdf,.docx,.doc"
              onChange={handleFileChange}
              className="hidden"
              id="file-upload"
            />
            <label
              htmlFor="file-upload"
              className="inline-block px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 cursor-pointer transition-colors"
            >
              파일 선택
            </label>
          </div>

          {file && (
            <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <p className="text-sm font-medium text-purple-900">
                선택된 파일: {file.name}
              </p>
              <p className="text-xs text-purple-600 mt-1">
                크기: {(file.size / 1024).toFixed(2)} KB
              </p>
            </div>
          )}

          <button
            onClick={handleSummarize}
            disabled={!file || loading}
            className="w-full mt-6 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                요약 생성 중...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                AI 요약 시작
              </>
            )}
          </button>
        </div>

        {/* 요약 결과 */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">요약 결과</h2>

          {summary ? (
            <div className="prose max-w-none">
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6">
                <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                  {summary}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
              <Sparkles className="w-12 h-12 mb-4" />
              <p className="text-sm">문서를 업로드하고 요약을 시작하세요</p>
            </div>
          )}
        </div>
      </div>

      {/* 안내 */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-bold text-blue-900 mb-2">💡 사용 가이드</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• 계약서, 보고서 등의 문서를 업로드하면 AI가 핵심 내용을 요약합니다</li>
          <li>• PDF 또는 DOCX 형식의 파일만 지원됩니다</li>
          <li>• 요약은 보통 30초 이내에 완료됩니다</li>
        </ul>
      </div>
    </div>
  );
}
